import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

const FRIENDLY_ERRORS = {
  'Invalid login credentials': 'Incorrect email or password.',
  'Email not confirmed': 'Please confirm your email before signing in.',
  'User already registered': 'An account with this email already exists.',
  'Password should be at least 6 characters': 'Password must be at least 6 characters.',
  'Unable to validate email address: invalid format': 'Please enter a valid email address.',
  'signup_disabled': 'Sign ups are currently disabled.',
}

function friendlyError(msg) {
  if (!msg) return 'Something went wrong. Please try again.'
  for (const [key, val] of Object.entries(FRIENDLY_ERRORS)) {
    if (msg.includes(key)) return val
  }
  return msg
}

// ── Eye icon ──────────────────────────────────────────────────────────────────
function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  )
}

// ── Password field ────────────────────────────────────────────────────────────
function PasswordInput({ value, onChange, placeholder = '••••••••', minLength }) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        required
        minLength={minLength}
        className="w-full px-4 py-3 pr-11 border border-gray-200 focus:border-black focus:outline-none transition-colors"
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        <EyeIcon open={show} />
      </button>
    </div>
  )
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function AuthModal({ isOpen, onClose }) {
  // 'login' | 'signup' | 'email-sent' | 'forgot' | 'reset-sent'
  const [view, setView]               = useState('login')
  const [email, setEmail]             = useState('')
  const [password, setPassword]       = useState('')
  const [displayName, setDisplayName] = useState('')
  const [age, setAge]                 = useState('')
  const [error, setError]             = useState('')
  const [loading, setLoading]         = useState(false)

  const { signIn, signUp } = useAuth()

  if (!isOpen) return null

  function close() {
    if (loading) return
    onClose()
    // Reset after fade — delay keeps UI clean
    setTimeout(() => {
      setView('login')
      setEmail('')
      setPassword('')
      setDisplayName('')
      setAge('')
      setError('')
    }, 300)
  }

  function switchTo(nextView) {
    setError('')
    setPassword('')
    setDisplayName('')
    setAge('')
    setView(nextView)
  }

  // ── Handlers ────────────────────────────────────────────────────────────────

  async function handleLoginOrSignup(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (view === 'signup') {
        const result = await signUp(email, password, displayName, age)
        // If no session, email confirmation is required
        if (!result?.session) {
          setView('email-sent')
        } else {
          close()
        }
      } else {
        await signIn(email, password)
        close()
      }
    } catch (err) {
      setError(friendlyError(err.message))
    } finally {
      setLoading(false)
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (err) throw err
      setView('reset-sent')
    } catch (err) {
      setError(friendlyError(err.message))
    } finally {
      setLoading(false)
    }
  }

  // ── Views ───────────────────────────────────────────────────────────────────

  const views = {

    login: (
      <>
        <h2 className="text-2xl font-semibold mb-8">Welcome back</h2>
        {error && <div className="mb-6 p-3 bg-red-50 text-red-600 text-sm">{error}</div>}
        <form onSubmit={handleLoginOrSignup} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full px-4 py-3 border border-gray-200 focus:border-black focus:outline-none transition-colors"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Password</label>
              <button
                type="button"
                onClick={() => switchTo('forgot')}
                className="text-xs text-gray-500 hover:text-black transition-colors"
              >
                Forgot password?
              </button>
            </div>
            <PasswordInput value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-black text-white font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-gray-600">
          Don't have an account?{' '}
          <button onClick={() => switchTo('signup')} className="text-black font-medium hover:underline">
            Create one
          </button>
        </p>
      </>
    ),

    signup: (
      <>
        <h2 className="text-2xl font-semibold mb-8">Create account</h2>
        {error && <div className="mb-6 p-3 bg-red-50 text-red-600 text-sm">{error}</div>}
        <form onSubmit={handleLoginOrSignup} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full px-4 py-3 border border-gray-200 focus:border-black focus:outline-none transition-colors"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <PasswordInput value={password} onChange={e => setPassword(e.target.value)} minLength={6} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-200 focus:border-black focus:outline-none transition-colors"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Age</label>
            <input
              type="number"
              value={age}
              onChange={e => setAge(e.target.value)}
              required
              min={16}
              max={99}
              className="w-full px-4 py-3 border border-gray-200 focus:border-black focus:outline-none transition-colors"
              placeholder="25"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-black text-white font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <button onClick={() => switchTo('login')} className="text-black font-medium hover:underline">
            Sign in
          </button>
        </p>
      </>
    ),

    'email-sent': (
      <div className="text-center py-4">
        <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center mx-auto mb-6">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
        </div>
        <h2 className="text-2xl font-semibold mb-3">Check your email</h2>
        <p className="text-gray-500 text-sm leading-relaxed mb-2">
          We've sent a confirmation link to
        </p>
        <p className="font-medium text-sm mb-6">{email}</p>
        <p className="text-gray-400 text-xs leading-relaxed mb-8">
          Click the link in the email to activate your account. Check your spam folder if you don't see it.
        </p>
        <button onClick={close} className="w-full py-3 bg-black text-white font-medium hover:bg-gray-800 transition-colors">
          Done
        </button>
      </div>
    ),

    forgot: (
      <>
        <button
          onClick={() => switchTo('login')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-black transition-colors mb-8"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back
        </button>
        <h2 className="text-2xl font-semibold mb-3">Forgot password?</h2>
        <p className="text-gray-500 text-sm mb-8">Enter your email and we'll send you a reset link.</p>
        {error && <div className="mb-6 p-3 bg-red-50 text-red-600 text-sm">{error}</div>}
        <form onSubmit={handleForgotPassword} className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full px-4 py-3 border border-gray-200 focus:border-black focus:outline-none transition-colors"
              placeholder="your@email.com"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-black text-white font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {loading ? 'Sending…' : 'Send Reset Link'}
          </button>
        </form>
      </>
    ),

    'reset-sent': (
      <div className="text-center py-4">
        <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center mx-auto mb-6">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h2 className="text-2xl font-semibold mb-3">Reset link sent</h2>
        <p className="text-gray-500 text-sm leading-relaxed mb-2">
          We've sent a password reset link to
        </p>
        <p className="font-medium text-sm mb-6">{email}</p>
        <p className="text-gray-400 text-xs leading-relaxed mb-8">
          Check your spam folder if you don't see it within a few minutes.
        </p>
        <button onClick={() => switchTo('login')} className="w-full py-3 bg-black text-white font-medium hover:bg-gray-800 transition-colors">
          Back to Sign In
        </button>
      </div>
    ),
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={close}
      />
      <div className="relative bg-white w-full max-w-md mx-4 p-8">
        {!['email-sent', 'reset-sent'].includes(view) && (
          <button
            onClick={close}
            className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {views[view]}
      </div>
    </div>
  )
}
